import { Button } from '@efexteam/components';
import useBoolean from '@main-hooks/useBoolean';
import { Alert, Box, Typography } from '@mui/material';
import DateService from '@utils/DateService';
import { FileService } from '@utils/downloadElement';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  iconDownloadWhite,
  iconExcelWhite,
  stateAccount,
} from '../../../assets';
import { Country } from '../../../configs/language';
import '../../../pages/dashboard/dashboard.scss';
import { selectProfileState } from '../../../pages/profile/store/selectors';
import { selectSelectedLanguage } from '../../../pages/store/language/selectors';
import accountstatusSvc from '../../../services/accountStatus/accountstatusSvc';

interface SecondStepAccountStatusProps {
  keyDateIssue: string;
  valueDateIssue: string;
}

const SecondStepAccountStatus = ({
  keyDateIssue,
  valueDateIssue,
}: SecondStepAccountStatusProps) => {
  const { t } = useTranslation();
  const userProfile = useSelector(selectProfileState);
  const selectLanguage = useSelector(selectSelectedLanguage);
  const [
    isErrorMessage,
    { setTrue: showErrorMessage, setFalse: hideErrorMessage },
  ] = useBoolean(false);

  const handleDownload = async (type: string) => {
    const response = await accountstatusSvc.reportAccountStatus({
      month: valueDateIssue.split('-')[0],
      year: valueDateIssue.split('-')[1],
      type,
      country: (userProfile.data?.company?.country as Country)
        ? (userProfile.data?.company?.country as Country)
        : selectLanguage.country,
      language: selectLanguage.language,
    });
    if (response.url) {
      const filePath = response.url;
      const anchor = document.createElement('a');
      const nameBase = filePath.split('/');
      const name = nameBase[nameBase.length - 1];
      anchor.href = filePath;
      anchor.download = name;
      anchor.target = '_blank';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    }
  };

  const handleDownloadPDF = async () => {
    const [monthStr, yearStr] = valueDateIssue.split('-');
    const month = Number(monthStr);
    const year = Number(yearStr);

    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);

    try {
      const blobResponse = await accountstatusSvc.reportAccountStatusPDF({
        startDate: DateService.formatDate(startDate),
        endDate: DateService.formatDate(endDate),
        enterpriseId: userProfile.data?.company?.enterpriseId ?? '',
        language: selectLanguage.language,
      });

      FileService.downloadBlobPDF(
        blobResponse,
        `AccountStatus-${monthStr}-${yearStr}.pdf`
      );
    } catch (error) {
      console.error('handleDownloadPDF::error', error);
      showErrorMessage();
    }
  };

  return (
    <>
      <img
        src={stateAccount}
        alt="state-account"
        className="max-w-[200px] mx-auto mb-4"
      />
      <Box className="modal-header flex flex-shrink-0 items-center justify-between border-b border-gray-200 rounded-t-md">
        <Typography variant="h5">{t('yourAccountStateGenerated')}!</Typography>
      </Box>
      <Box className="md:w-10/12 w-full mx-auto">
        <Box className="bg-[--colors-primary-200] rounded-lg p-4">
          <Box className="flex flex-col gap-8">
            <Box className="flex justify-between items-center">
              <Typography variant="body1">{t('type')}:</Typography>
              <Typography variant="body1" className="!font-bold">
                {t('incomeAccount')}
              </Typography>
            </Box>
            <Box className="flex justify-between items-center">
              <Typography variant="body1">{t('issueMonth')}:</Typography>
              <Typography variant="body1" className="!font-bold">
                {keyDateIssue}
              </Typography>
            </Box>
            <Box className="flex justify-around items-center my-4">
              <Button
                appearance="primary"
                onClick={() => {
                  handleDownloadPDF();
                }}
                endIcon={
                  <img
                    className="w-[20px]"
                    src={iconDownloadWhite}
                    alt="iconDownloadWhite"
                  />
                }
              >
                {t('pdf')}
              </Button>
              <Button
                appearance="primary"
                onClick={() => {
                  handleDownload('btnExcel');
                }}
                endIcon={
                  <img
                    className="w-[20px]"
                    src={iconExcelWhite}
                    alt="iconExcelWhite"
                  />
                }
              >
                {t('excel')}
              </Button>
            </Box>
          </Box>
        </Box>
        {isErrorMessage && (
          <Alert severity="error" onClose={() => hideErrorMessage()}>
            {t('errorDownload')}
          </Alert>
        )}
      </Box>
    </>
  );
};

export default SecondStepAccountStatus;
